import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BadgeCheck, Building2, GraduationCap, Loader2, ShieldCheck } from 'lucide-react';
import { apiService } from '../services/apiService';

export type VerificationKind = 'employment' | 'education';

export interface VerificationStatus {
  status: 'unverified' | 'pending' | 'verified';
  companyEmail?: string;
  companyDomain?: string;
  verifiedAt?: string;
}

interface Props {
  /** Which verifications to offer. Defaults to both. */
  kinds?: VerificationKind[];
  onStatusChange?: (statuses: Record<VerificationKind, VerificationStatus>) => void;
}

const KIND_META: Record<VerificationKind, {
  tab: string;
  icon: React.ReactNode;
  prompt: string;
  placeholder: string;
  verifiedLabel: string;
}> = {
  employment: {
    tab: 'Employer',
    icon: <Building2 className="w-4 h-4" />,
    prompt: 'Verify your current employer with your company email to earn a verified badge.',
    placeholder: 'you@company.com',
    verifiedLabel: 'Employer verified',
  },
  education: {
    tab: 'School',
    icon: <GraduationCap className="w-4 h-4" />,
    prompt: 'Verify your school enrollment with your school-issued email (e.g. .edu).',
    placeholder: 'you@university.edu',
    verifiedLabel: 'Enrollment verified',
  },
};

const DEFAULT_STATUSES: Record<VerificationKind, VerificationStatus> = {
  employment: { status: 'unverified' },
  education: { status: 'unverified' },
};

const VerificationsCard: React.FC<Props> = ({ kinds = ['education', 'employment'], onStatusChange }) => {
  const [statuses, setStatuses] = useState<Record<VerificationKind, VerificationStatus>>(DEFAULT_STATUSES);
  const [activeKind, setActiveKind] = useState<VerificationKind>(kinds[0]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const setAndNotify = (next: Record<VerificationKind, VerificationStatus>) => {
    setStatuses(next);
    onStatusChange?.(next);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.get('employment-verification/statuses');
        setAndNotify({ ...DEFAULT_STATUSES, ...(res.data.data || {}) });
      } catch {
        /* card just shows the default unverified state */
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = statuses[activeKind];

  const requestCode = async () => {
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await apiService.post('employment-verification/request', {
        companyEmail: email,
        kind: activeKind,
      });
      setAndNotify({ ...statuses, [activeKind]: res.data.data });
      setEmail('');
      toast.success('Verification code sent — check that inbox.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not send verification code.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCode = async () => {
    if (!code) return;
    setSubmitting(true);
    try {
      const res = await apiService.post('employment-verification/confirm', {
        code,
        kind: activeKind,
      });
      setAndNotify({ ...statuses, [activeKind]: res.data.data });
      setCode('');
      toast.success('Verified!');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Incorrect or expired code.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-gray-900">Verification</h3>
      </div>

      {kinds.length > 1 && (
        <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
          {kinds.map(kind => (
            <button
              key={kind}
              onClick={() => { setActiveKind(kind); setEmail(''); setCode(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeKind === kind ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {KIND_META[kind].icon}
              {KIND_META[kind].tab}
              {statuses[kind].status === 'verified' && <BadgeCheck className="w-3.5 h-3.5 text-green-600" />}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking status…
        </div>
      ) : current.status === 'verified' ? (
        <div className="flex items-start gap-3">
          <BadgeCheck className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-green-700">{KIND_META[activeKind].verifiedLabel}</p>
            <p className="text-sm text-gray-600 flex items-center gap-1">
              {KIND_META[activeKind].icon} {current.companyDomain}
            </p>
          </div>
        </div>
      ) : current.status === 'pending' ? (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            We emailed a 6-digit code to <strong>{current.companyEmail}</strong>. Enter it below.
          </p>
          <div className="flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 tracking-widest"
            />
            <button
              onClick={confirmCode}
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
            >
              Verify
            </button>
          </div>
          <button
            onClick={() => setAndNotify({ ...statuses, [activeKind]: { status: 'unverified' } })}
            className="mt-2 text-xs text-gray-500 hover:underline"
          >
            Use a different email
          </button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-600 mb-3">{KIND_META[activeKind].prompt}</p>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && requestCode()}
              placeholder={KIND_META[activeKind].placeholder}
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2"
            />
            <button
              onClick={requestCode}
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
            >
              {submitting ? 'Sending…' : 'Send code'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationsCard;
